<script setup>
import { computed, ref, watch } from 'vue'
import { useAnalytics } from '@/shared/analytics'
import { BRADLEY_ANALYTICS_EVENTS } from '../analytics/bradley-analytics.constants.js'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  maxDocuments: {
    type: Number,
    default: 20,
  },
  similarityThreshold: {
    type: Number,
    default: 0.17,
  },
})

const emit = defineEmits(['close', 'updateSettings'])

const { trackEvent } = useAnalytics()

// Valores dos sliders (sincronizados com as props quando o modal abre)
const maxDocuments = ref(props.maxDocuments)
const similarityThreshold = ref(props.similarityThreshold)

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    maxDocuments.value = props.maxDocuments
    similarityThreshold.value = props.similarityThreshold
  }
})

// Calcular posição do tooltip baseado no valor do slider
const maxDocumentsPosition = computed(() => {
  const min = 0
  const max = 100
  const percentage = ((maxDocuments.value - min) / (max - min)) * 100
  return `${percentage}%`
})

const similarityThresholdPosition = computed(() => {
  const min = 0.10
  const max = 1.00
  const percentage = ((similarityThreshold.value - min) / (max - min)) * 100
  return `${percentage}%`
})

function handleCancel() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.CANCEL, { surface: 'settings_modal' })
  emit('close')
}

function handleCloseButton() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.CLOSE_BUTTON, { surface: 'settings_modal' })
  emit('close')
}

function handleUpdate() {
  trackEvent(BRADLEY_ANALYTICS_EVENTS.UPDATE_SETTINGS, {
    surface: 'settings_modal',
    max_documents: maxDocuments.value,
    similarity_threshold: similarityThreshold.value,
  })
  emit('updateSettings', {
    maxDocuments: maxDocuments.value,
    similarityThreshold: similarityThreshold.value,
  })
  emit('close')
}

function formatThreshold(value) {
  return Number(value).toFixed(2)
}
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 flex items-center justify-center z-50 p-4"
        @click.self="handleCancel"
      >
        <div class="inset-0 z-[-1] bg-black opacity-40 w-full h-full absolute top-0 left-0" />
        <div class="bg-white rounded-2xl w-full max-w-2xl flex flex-col shadow-2xl">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-dss-surface-200">
            <h2 class="font-inter text-xl font-bold leading-none tracking-[0.005em]">
              {{ $t('newBradley.settingsModal.title') }}
            </h2>
            <button
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              @click="handleCloseButton"
            >
              <i class="pi pi-times text-gray-600 text-lg" />
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto px-6 py-6 space-y-8">
            <!-- Maximum number of documents -->
            <div>
              <h3 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-2">
                {{ $t('newBradley.settingsModal.maxDocumentsTitle') }}
              </h3>
              <p class="font-hellix text-sm font-medium leading-[163%] text-gray-600 mb-4">
                {{ $t('newBradley.settingsModal.maxDocumentsDescription') }}
              </p>

              <!-- Slider Container -->
              <div class="relative pt-8">
                <!-- Value Display -->
                <div
                  class="absolute top-0 bg-dss-primary-500 text-white rounded-lg text-sm font-semibold slider-value"
                  :style="{ left: maxDocumentsPosition }"
                >
                  {{ maxDocuments }}
                </div>

                <!-- Slider -->
                <input
                  v-model.number="maxDocuments"
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  class="slider"
                  :style="{ '--value': maxDocumentsPosition }"
                >

                <!-- Range Labels -->
                <div class="flex justify-between mt-2 text-xs text-gray-500">
                  <span>0</span>
                  <span>100</span>
                </div>
              </div>
            </div>

            <!-- Document similarity threshold -->
            <div>
              <h3 class="font-inter text-base font-semibold leading-6 tracking-[0.005em] mb-2">
                {{ $t('newBradley.settingsModal.similarityThresholdTitle') }}
              </h3>

              <!-- Slider Container -->
              <div class="relative pt-8">
                <!-- Value Display -->
                <div
                  class="absolute top-0 bg-dss-primary-500 text-white rounded-lg text-sm font-semibold slider-value"
                  :style="{ left: similarityThresholdPosition }"
                >
                  {{ formatThreshold(similarityThreshold) }}
                </div>

                <!-- Slider -->
                <input
                  v-model.number="similarityThreshold"
                  type="range"
                  min="0.10"
                  max="1.00"
                  step="0.01"
                  class="slider"
                  :style="{ '--value': similarityThresholdPosition }"
                >

                <!-- Range Labels -->
                <div class="flex justify-between mt-2 text-xs text-gray-500">
                  <span>0.10</span>
                  <span>1.00</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-dss-surface-200 flex justify-end gap-3">
            <button
              class="font-inter py-2 px-3 border border-[#B0B2DA] text-sm hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors cursor-pointer"
              @click="handleCancel"
            >
              {{ $t('newBradley.settingsModal.cancel') }}
            </button>
            <button
              class="font-hellix font-medium text-sm leading-none py-2 px-3 bg-dss-primary-500 hover:bg-dss-primary-600 text-white rounded-lg transition-colors cursor-pointer"
              @click="handleUpdate"
            >
              {{ $t('newBradley.settingsModal.updateSettings') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
/* Slider Styles */
.slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  outline: none;
  background: #e5e7eb;
}

.slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(to right, var(--p-primary-500) 0%, var(--p-primary-500) var(--value), #e5e7eb var(--value), #e5e7eb 100%);
}

.slider::-moz-range-track {
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: #e5e7eb;
}

.slider::-moz-range-progress {
  height: 3px;
  border-radius: 2px;
  background: var(--p-primary-500);
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  border: 2px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: -7.5px;
}

.slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  border: 2px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.slider::-webkit-slider-thumb:hover {
  border-color: #9ca3af;
}

.slider::-moz-range-thumb:hover {
  border-color: #9ca3af;
}

/* Position value above slider thumb with white circle */
.slider-value {
  transform: translateX(-50%);
  pointer-events: none;
  transition: left 0.1s ease;
  position: relative;
  width: 47px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slider-value::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid var(--p-primary-500);
}
</style>
